import { forwardRef, memo, useMemo, useRef } from 'react'

import { twMerge } from 'tailwind-merge'

import { Link as RouterLink } from '@cedarjs/router'

import { useMergeRefs } from 'src/hooks/use-merge-refs'

import Icon from '../Icon/Icon'

import { getLinkProps, isInternalLink, LinkActionProps } from './util'

type Props = {
  ariaLabel?: string
  className?: string
  children: React.ReactNode
  /** Force the link to open in a new tab, even for internal routes. */
  openInNewTab?: boolean
} & LinkActionProps

const Link = forwardRef<HTMLElement, Props>((props, propRef) => {
  const {
    ariaLabel,
    className: propClassName,
    children,
    externalHref,
    onClick,
    openInNewTab,
  } = props
  const className = twMerge(
    // TODO: remove important styling once we've eliminated all antd tables
    '!text-link inline cursor-pointer text-left hover:!underline focus:!underline focus-visible:!outline-1 !outline-black rounded-xs',
    propClassName
  )
  const internalRef = useRef<HTMLElement>(null)
  const ref = useMergeRefs(propRef || null, internalRef)

  const content = useMemo(
    () => (
      <>
        {children}
        {!!externalHref && (
          <>
            &nbsp;
            <Icon.FaArrowUpRightFromSquare />
          </>
        )}
      </>
    ),
    [children, externalHref]
  )

  const { rel, target } = getLinkProps(!!externalHref || !!openInNewTab)

  if (isInternalLink(props)) {
    // RouterLink intercepts clicks for SPA navigation, which defeats
    // target="_blank". Render a plain <a> when we need a new tab.
    if (openInNewTab) {
      return (
        <a
          aria-label={ariaLabel}
          className={className}
          href={props.internalRoute}
          ref={ref as React.Ref<HTMLAnchorElement>}
          rel={rel}
          target={target}
        >
          {content}
        </a>
      )
    }
    return (
      <RouterLink
        aria-label={ariaLabel}
        className={className}
        ref={ref}
        rel={rel}
        target={target}
        to={props.internalRoute}
      >
        {content}
      </RouterLink>
    )
  }

  if (externalHref) {
    return (
      <a
        aria-label={ariaLabel}
        className={className}
        href={externalHref}
        onClick={onClick}
        ref={ref}
        rel={rel}
        target={target}
      >
        {content}
      </a>
    )
  }

  return (
    <button aria-label={ariaLabel} className={className} onClick={onClick} ref={ref} type="button">
      {content}
    </button>
  )
})

export default memo(Link)
